import { useContext, useEffect, useState } from "react";
import {
  getValidationMessages,
  createValidationMessages,
  updateValidationMessages,
  deleteValidationMessages,
  exportValidationMessages,
  importValidationMessages,
} from "../services/configurations/validation-messages";
import { AppContext } from "../context";

type UseType = "DATA" | "ACTIONS";
type ActionType = "CREATE" | "UPDATE" | "DELETE" | "DOWNLOAD" | "IMPORT";

const handleValidationMessagesActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createValidationMessages,
        fnMsg: t("ValidationMessages.validationMessagesApiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateValidationMessages,
        fnMsg: t("ValidationMessages.validationMessagesApiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteValidationMessages,
        fnMsg: t("ValidationMessages.validationMessagesApiDeleteLabel"),
      };
    case "DOWNLOAD":
      return {
        actionFn: exportValidationMessages,
        fnMsg: t("ValidationMessages.validationMessagesApiDownloadLabel"),
      };
    case "IMPORT":
      return {
        actionFn: importValidationMessages,
        fnMsg: t("ValidationMessages.validationMessagesApiImportLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useValidationMessages = (type: UseType, messageId?: number) => {
  const {
    t,
    toast,
    appState: { selectedApp },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<any | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || "0";

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const validationMessagesAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg } = handleValidationMessagesActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data, appid))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (type === "DATA" && appid) {
      (async function () {
        await Promise.resolve(getValidationMessages(appid))
          .then((response) => {
            setData(response);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, toast, reload, type, messageId]);

  return {
    data,
    validationMessagesAction,
    reloadFn,
  };
};
